
import Vue from 'vue'
import { cover } from 'intrinsic-scale'
import { mapper } from '@/stores/store'
import { Categories } from '@/constants/elements'
import isBrightColor from '@/helpers/is-bright-color'

export default Vue.extend({
  data() {
    return {
      patterns: {
        checkered: new Image(),
        checkeredDesktop: new Image(),
        light: new Image(),
        lightDesktop: new Image(),
        dark: new Image(),
        darkDesktop: new Image(),
      },
      colorEventX: 0,
      colorEventY: 0,
    }
  },
  computed: {
    ...mapper('activeCategory'),
    ...mapper(['isLandscape', 'pickingColor']),
    canvas(): HTMLCanvasElement {
      return this.$refs.canvas as HTMLCanvasElement
    },
    showBackground(): boolean {
      return !this.$typedStore.pin.features.background.hidden
    },
    backgroundColor(): string {
      return this.$typedStore.pin.features.background.color
    },
    isBackgroundFeature(): boolean {
      return this.activeCategory === Categories.BACKGROUND
    },
  },
  watch: {
    pickingColor() {
      if (this.pickingColor) {
        this.buildColorPalette()
        addEventListener('pointerdown', this.pointerMove, { passive: false })
        addEventListener('pointermove', this.pointerMove, { passive: false })
        addEventListener('pointerup', this.pointerUp, { passive: false })
        this.saveUndoState()
      } else {
        this.removeColorPalette()
        this.createPatternedBackground()
        removeEventListener('pointerdown', this.pointerMove)
        removeEventListener('pointermove', this.pointerMove)
        removeEventListener('pointerup', this.pointerUp)
      }
    },
    backgroundColor: {
      immediate: true,
      handler() {
        // Set body background to fill out on iOS devices
        // document.body.style.background = this.backgroundColor

        // Keep showing color wheel when picking color
        if (this.pickingColor) return

        // Update background
        this.$nextTick(() => {
          this.createPatternedBackground()
        })
      },
    },
    showBackground() {
      // Recreate patterened background when showBackground changes
      this.createPatternedBackground()
    },
  },
  mounted() {
    this.$root.$on('resize', this.onResize)

    // Load patterns
    this.loadPatterns().then(() => {
      this.createPatternedBackground()
    })

    this.onResize()
  },
  methods: {
    ...mapper('pin', ['setColor', 'setActiveBackgroundPatternDesktop', 'saveUndoState']),
    ...mapper(['setPickingColor']),
    onResize() {
      this.canvas.width = window.innerWidth
      this.canvas.height = window.innerHeight

      if (this.pickingColor) {
        this.buildColorPalette()
      } else {
        this.createPatternedBackground()
      }
    },
    async loadPatterns(): Promise<void> {
      return new Promise(resolve => {
        this.patterns.checkered.src = '/checkeredBg.png'
        this.patterns.checkeredDesktop.src = '/desktopCheckeredBg.png'
        this.patterns.light.src = '/patternBg3.png'
        this.patterns.lightDesktop.src = '/desktopPatternBg.png'
        this.patterns.dark.src = '/patternBgDark.png'
        this.patterns.darkDesktop.src = '/desktopPatternBgDark.png'

        let imagesLoaded = 0
        Object.values(this.patterns).forEach(pattern => {
          pattern.onload = () => {
            imagesLoaded++
            if (imagesLoaded === Object.keys(this.patterns).length - 1) {
              resolve()
            }
          }
        })
      })
    },
    rgbToHex(r: number, g: number, b: number): string {
      return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
    },
    pointerMove(e: PointerEvent) {
      e.preventDefault()

      const x = e.clientX
      const y = e.clientY
      this.colorEventX = Math.max(1, Math.min(window.innerWidth - 1, x))
      this.colorEventY = Math.max(1, Math.min(window.innerHeight - 1, y))

      const pixel = this.canvas
        .getContext('2d')!
        .getImageData(this.colorEventX, this.colorEventY, 1, 1)
      const color = this.rgbToHex(pixel.data[0], pixel.data[1], pixel.data[2])
      this.setColor(color)
    },
    pointerUp() {
      this.setPickingColor(false)
      const pixel = this.canvas
        .getContext('2d')!
        .getImageData(this.colorEventX, this.colorEventY, 1, 1)
      const color = this.rgbToHex(pixel.data[0], pixel.data[1], pixel.data[2])
      this.setColor(color)
    },
    createPatternedBackground() {
      const color = this.backgroundColor
      const ctx = this.canvas.getContext('2d')!
      const width = ctx.canvas.width
      const height = ctx.canvas.height

      if (this.showBackground) {
        // Update body background to fill out on iOS devices
        // document.body.style.background = color

        // fill the canvas with white so you dont see the background texture on desktop
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, width, height)

        // Draw spotlight
        var gradient = ctx.createRadialGradient(
          (width / 2) * (this.isLandscape ? 0.5 : 1),
          (height / 2) * (this.isLandscape ? 1 : 0.65),
          5,
          (width / 2) * (this.isLandscape ? 0.5 : 1),
          (height / 2) * (this.isLandscape ? 1 : 0.65),
          250
        )
        const lowColor = color + '40'
        const halfColor = color + '99'
        gradient.addColorStop(0, lowColor)
        gradient.addColorStop(0.5, halfColor)
        gradient.addColorStop(1, color)
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, width, height)

        // Draw image
        let imageToDraw = this.isLandscape ? this.patterns.lightDesktop : this.patterns.light
        if (isBrightColor(this.backgroundColor)) {
          imageToDraw = this.isLandscape ? this.patterns.darkDesktop : this.patterns.dark
        }
        this.setActiveBackgroundPatternDesktop(
          isBrightColor(this.backgroundColor)
            ? this.patterns.darkDesktop
            : this.patterns.lightDesktop
        )

        const coverSize = cover(width, height, imageToDraw.width, imageToDraw.height)
        ctx.drawImage(
          imageToDraw,
          0,
          0,
          imageToDraw.width,
          imageToDraw.height,
          coverSize.x,
          coverSize.y,
          coverSize.width,
          coverSize.height
        )
      } else {
        // Update body background to fill out on iOS devices
        // const checkeredLightColor = '#e6e6e6'
        // document.body.style.background = checkeredLightColor
        this.setActiveBackgroundPatternDesktop(null)

        let imageToDraw = this.isLandscape
          ? this.patterns.checkeredDesktop
          : this.patterns.checkered
        const coverSize = cover(width, height, imageToDraw.width, imageToDraw.height)
        ctx.drawImage(
          imageToDraw,
          0,
          0,
          imageToDraw.width,
          imageToDraw.height,
          coverSize.x,
          coverSize.y,
          coverSize.width,
          coverSize.height
        )
      }
    },
    removeColorPalette() {
      this.canvas.getContext('2d')!.clearRect(0, 0, this.canvas.width, this.canvas.height)
    },
    buildColorPalette() {
      // Fixed swatches
      const colors = [
        '#feeaeb',
        '#f8edf3',
        '#daace5',
        '#fcd8c5',
        '#fac491',
        '#f7bb8d',
        '#ff8b5d',
        '#e86363',
        '#ffb600',
        '#01ad32',
        '#00befd',
        '#fd71bc',
        '#ff052a',
        '#842ab2',
        '#334a89',
        '#ded9ed',
      ]
      const swatchTotal = colors.length
      const swatchRows = window.innerWidth <= 460 ? 2 : 1
      const swatchColumns = swatchTotal / swatchRows
      const ctx = this.canvas.getContext('2d')!
      const width = ctx.canvas.width
      const height = ctx.canvas.height
      const swatchAreaHeight = (width / (swatchTotal / swatchRows)) * swatchRows
      const gradientHeight = height - swatchAreaHeight
      const swatchWidth = width / swatchColumns
      const swatchHeight = swatchAreaHeight / swatchRows

      // Create color gradient
      var gradient = ctx.createLinearGradient(0, 0, width, 0)
      gradient.addColorStop(99.28 / 100, '#FF0008')
      gradient.addColorStop(86.35 / 100, '#FF00EB')
      gradient.addColorStop(76.01 / 100, '#0E00FF')
      gradient.addColorStop(67.22 / 100, '#00EDFF')
      gradient.addColorStop(58.95 / 100, '#00FFF0')
      gradient.addColorStop(52.74 / 100, '#00FF00')
      gradient.addColorStop(43.43 / 100, '#FFFE00')
      gradient.addColorStop(32.58 / 100, '#FFE600')
      gradient.addColorStop(19.65 / 100, '#F77600')
      gradient.addColorStop(0, '#FF0000')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, width, gradientHeight)

      // Create color swatches
      for (let i = 0; i < swatchTotal; i++) {
        const swatchX = (i % swatchColumns) * swatchWidth
        const swatchY = Math.floor(i / swatchColumns) * (swatchAreaHeight / swatchRows)
        ctx.fillStyle = colors[i]
        ctx.fillRect(swatchX, height - swatchAreaHeight + swatchY, swatchWidth, swatchHeight)
      }

      // Create semi transparent gradient (white -> trans. -> black)
      gradient = ctx.createLinearGradient(0, 0, 0, height - swatchAreaHeight)
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)')
      gradient.addColorStop(0.25, 'rgba(255, 255, 255, 0)')
      gradient.addColorStop(0.5, 'rgba(0,     0,   0, 0)')
      gradient.addColorStop(1, 'rgba(0,     0,   0, 1)')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, width, gradientHeight)
    },
  },
})
